import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Zap, ArrowLeft, Save, RotateCcw, Settings, 
  Phone, MessageSquare, Mail, Calendar, Clock, Info,
  GripVertical
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  call: Phone,
  whatsapp: MessageSquare,
  email: Mail,
  meeting: Calendar,
  task: Clock
};

const AINextBestAction = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config && originalConfig) {
      setHasChanges(JSON.stringify(config) !== JSON.stringify(originalConfig));
    }
  }, [config, originalConfig]);

  const fetchConfig = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/ai-intelligence/modules/next_best_action`, {
        params: { token }
      });
      
      if (response.data.success) {
        setConfig(response.data.module);
        setOriginalConfig(JSON.parse(JSON.stringify(response.data.module)));
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = sessionStorage.getItem('auth_token');
      const response = await axios.put(
        `${API}/api/ai-intelligence/modules/next_best_action?token=${token}`,
        {
          enabled: config.enabled,
          settings: config.settings
        }
      );
      
      if (response.data.success) {
        setOriginalConfig(JSON.parse(JSON.stringify(config)));
        toast.success('Configuration saved successfully');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const token = sessionStorage.getItem('auth_token');
      await axios.post(`${API}/api/ai-intelligence/modules/next_best_action/reset?token=${token}`);
      toast.success('Reset to defaults');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to reset');
    }
  };

  const updateSetting = (key, value) => {
    setConfig(prev => ({
      ...prev,
      settings: { ...prev.settings, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        </div>
      </div>
    );
  }

  const actionsPriority = config?.settings?.actions_priority || ['call', 'whatsapp', 'email', 'meeting', 'task'];

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="next-best-action-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/ai-intelligence')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Next Best Action</h1>
              <p className="text-sm text-slate-500">AI-powered action recommendations</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              Unsaved Changes
            </Badge>
          )}
          <Switch
            checked={config?.enabled}
            onCheckedChange={(checked) => setConfig(prev => ({ ...prev, enabled: checked }))}
            data-testid="module-toggle"
          />
          <span className={`text-sm font-medium ${config?.enabled ? 'text-green-600' : 'text-slate-500'}`}>
            {config?.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      {!config?.enabled && (
        <Alert className="bg-amber-50 border-amber-200">
          <Info className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            This module is currently disabled. Enable it to use AI action recommendations.
          </AlertDescription>
        </Alert>
      )}

      {/* Display Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Display Settings
          </CardTitle>
          <CardDescription>
            Configure where and how recommendations appear
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Show in Lead Detail Page</Label>
              <p className="text-sm text-slate-500">Display AI recommendations on contact pages</p>
            </div>
            <Switch
              checked={config?.settings?.show_in_lead_detail}
              onCheckedChange={(checked) => updateSetting('show_in_lead_detail', checked)}
              data-testid="show-in-detail"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Show Confidence Score</Label>
              <p className="text-sm text-slate-500">Display AI confidence level with recommendations</p>
            </div>
            <Switch
              checked={config?.settings?.show_confidence_score}
              onCheckedChange={(checked) => updateSetting('show_confidence_score', checked)}
              data-testid="show-confidence"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Auto-Create Follow-up Task</Label>
              <p className="text-sm text-slate-500">Automatically create a task from AI suggestion</p>
            </div>
            <Switch
              checked={config?.settings?.auto_create_task}
              onCheckedChange={(checked) => updateSetting('auto_create_task', checked)}
              data-testid="auto-create-task"
            />
          </div>
        </CardContent>
      </Card>

      {/* Priority Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-600" />
            Recommendation Mode
          </CardTitle>
          <CardDescription>
            Set the aggressiveness of follow-up recommendations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            {['aggressive', 'balanced', 'conservative'].map((mode) => (
              <div
                key={mode}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all text-center ${
                  config?.settings?.priority_mode === mode
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => updateSetting('priority_mode', mode)}
                data-testid={`mode-${mode}`}
              >
                <span className="font-medium capitalize">{mode}</span>
                <p className="text-xs text-slate-500 mt-1">
                  {mode === 'aggressive' && 'More frequent follow-ups'}
                  {mode === 'balanced' && 'Standard timing'}
                  {mode === 'conservative' && 'Less frequent follow-ups'}
                </p>
              </div>
            ))}
          </div>

          <div className="pt-4">
            <Label className="mb-2 block">Max Hours Since Last Contact</Label>
            <div className="flex items-center gap-4">
              <Input
                type="number"
                value={config?.settings?.max_hours_since_contact || 72}
                onChange={(e) => updateSetting('max_hours_since_contact', parseInt(e.target.value))}
                min={12}
                max={168}
                className="w-24"
                data-testid="max-hours"
              />
              <span className="text-sm text-slate-500">hours before suggesting re-engagement</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pattern Analysis */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Pattern Analysis
          </CardTitle>
          <CardDescription>
            Configure what patterns the AI should consider
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Consider Response Patterns</Label>
              <p className="text-sm text-slate-500">Learn from lead response behavior</p>
            </div>
            <Switch
              checked={config?.settings?.consider_response_patterns}
              onCheckedChange={(checked) => updateSetting('consider_response_patterns', checked)}
              data-testid="response-patterns"
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <Label className="font-medium">Consider Source Patterns</Label>
              <p className="text-sm text-slate-500">Learn from lead source effectiveness</p>
            </div>
            <Switch
              checked={config?.settings?.consider_source_patterns}
              onCheckedChange={(checked) => updateSetting('consider_source_patterns', checked)}
              data-testid="source-patterns"
            />
          </div>
        </CardContent>
      </Card>

      {/* Action Priority */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <GripVertical className="h-5 w-5 text-purple-600" />
            Action Priority Order
          </CardTitle>
          <CardDescription>
            Order of preference for action suggestions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {actionsPriority.map((action, index) => {
              const Icon = ACTION_ICONS[action] || Clock;
              return (
                <div
                  key={action}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                >
                  <span className="text-sm font-medium text-slate-400 w-6">{index + 1}.</span>
                  <Icon className="h-4 w-4 text-purple-600" />
                  <span className="capitalize">{action}</span>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            * Priority order is used when multiple actions are equally viable
          </p>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleReset}
          className="text-slate-600"
          data-testid="reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-2" />
          Reset to Defaults
        </Button>
        
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-purple-600 hover:bg-purple-700"
          data-testid="save-btn"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Configuration
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AINextBestAction;
